import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2 } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SupplierForm from '../../components/purchase/SupplierForm';
import api from '../../lib/api';

const Suppliers = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/login'); return; }
    setUser(JSON.parse(storedUser));
    fetchSuppliers();
  }, [navigate]);

  const fetchSuppliers = async (q = '') => {
    try {
      setLoading(true);
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const res = await api.get(`/suppliers${params}`);
      setSuppliers(res.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSuppliers(search);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingSupplier(null);
    fetchSuppliers(search);
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleDeactivate = async (supplier) => {
    if (!window.confirm(`Deactivate supplier "${supplier.name}"?`)) return;
    try {
      await api.delete(`/suppliers/${supplier.id}`);
      fetchSuppliers(search);
    } catch (err) {
      alert('Failed to deactivate supplier');
    }
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="suppliers-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="suppliers-title">Suppliers</h1>
            <button onClick={() => { setEditingSupplier(null); setShowForm(true); }} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm px-4 py-2 font-medium flex items-center gap-2" data-testid="add-supplier-button">
              <Plus className="w-5 h-5" /> Add Supplier
            </button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
                <input type="text" placeholder="Search by name, phone, NTN..." className="w-full pl-10 pr-3 py-2 border border-[#e5e7eb] rounded-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="supplier-search-input" />
              </div>
              <button type="submit" className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm font-medium" data-testid="supplier-search-button">Search</button>
            </form>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : suppliers.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280] mb-4">No suppliers found</p>
              <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm px-4 py-2 font-medium">Add Your First Supplier</button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="suppliers-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Type</th>
                    <th className="py-3 px-4">Phone</th>
                    <th className="py-3 px-4">City</th>
                    <th className="py-3 px-4">NTN</th>
                    <th className="py-3 px-4">Balance</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((s) => (
                    <tr key={s.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 font-medium text-[#1a1a2e]">{s.name}</td>
                      <td className="py-3 px-4 text-[#6b7280] capitalize">{s.supplier_type}</td>
                      <td className="py-3 px-4 text-[#6b7280]">{s.phone || '-'}</td>
                      <td className="py-3 px-4 text-[#6b7280]">{s.city || '-'}</td>
                      <td className="py-3 px-4 text-[#6b7280]">{s.ntn || '-'}</td>
                      <td className="py-3 px-4 font-semibold text-[#EF4444]">Rs. {parseFloat(s.balance).toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${s.is_active ? 'bg-[#DBEAFE] text-[#1E40AF]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(s)} className="text-[#1E40AF] hover:underline text-xs font-medium" data-testid={`edit-supplier-${s.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {s.is_active && (
                            <button onClick={() => handleDeactivate(s)} className="text-[#EF4444] hover:underline text-xs font-medium" data-testid={`deactivate-supplier-${s.id}`}>Deactivate</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
      {showForm && <SupplierForm supplier={editingSupplier} onClose={() => { setShowForm(false); setEditingSupplier(null); }} onSuccess={handleFormSuccess} />}
    </div>
  );
};

export default Suppliers;
